import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { listPatientsPage, type Patient } from '../api/unimedica';

// Port aus PatientPicker.svelte — Modal zur Aktenwahl (Chat, Pruefen).

const fmtDate = (d: string | null) => { if (!d) return '—'; const [y, m, day] = d.split('-'); return day && m && y ? `${day}.${m}.${y}` : d; };
const age = (d: string | null) => {
  if (!d) return '';
  const b = new Date(d), n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  if (n < new Date(n.getFullYear(), b.getMonth(), b.getDate())) a--;
  return isNaN(a) ? '' : `${a}J`;
};
const aktenNr = (pid: string) => { const n = pid.split('_')[0]; return /^\d+$/.test(n) ? `Akte ${n}` : ''; };

export default function PatientPicker({ onSelect, onCancel }: { onSelect: (p: Patient) => void; onCancel: () => void }) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [mehr, setMehr] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // #19 (10.10.2026): Vorher kamen nur die ersten 200 Akten und der Browser filterte darin — wer weiter
  // hinten stand, war hier unauffindbar, obwohl er unter „Patienten“ erschien. Jetzt sucht der Server.
  useEffect(() => {
    const ab = new AbortController();
    const t = setTimeout(() => {
      listPatientsPage({ q: query.trim(), limit: 200, signal: ab.signal })
        .then((d) => { setPatients(d.patients); setMehr(d.hat_mehr); setLoading(false); })
        .catch(() => {});
    }, query ? 250 : 0);
    return () => { clearTimeout(t); ab.abort(); };
  }, [query]);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const filtered = patients;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-6 pt-24" onClick={onCancel} role="presentation">
      <div className="flex max-h-[78dvh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl" onClick={(e) => e.stopPropagation()} role="presentation">
        <header className="border-b border-gray-200 px-5 pb-4 pt-5">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-500">Patientenwahl</div>
              <h2 className="mt-1 text-[22px] font-semibold tracking-tight text-ps-dark-900">Welche Akte?</h2>
            </div>
            <button onClick={onCancel} className="rounded-lg p-1.5 text-ps-dark-400 hover:bg-gray-100 hover:text-ps-dark-700" aria-label="Schliessen"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ps-dark-400" />
            <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, Patient-ID …" className="input-field pl-9 !text-sm" />
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-3 p-8">{[0, 1, 2].map((i) => <div key={i} className="space-y-2"><div className="shimmer h-3 w-1/2 rounded" /><div className="shimmer h-2 w-1/3 rounded" /></div>)}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mb-1 text-[18px] font-semibold text-ps-dark-500">Keine Treffer</div>
              <div className="text-[12px] text-ps-dark-400">Suche anpassen oder Patient anders schreiben.</div>
            </div>
          ) : filtered.map((p) => (
            <button key={p.patient_id} onClick={() => onSelect(p)}
              className="group grid w-full grid-cols-[1fr_auto] items-center gap-4 border-b border-gray-200 px-5 py-3.5 text-left transition-colors hover:bg-gray-50">
              <div className="min-w-0">
                <span className="text-[17px] font-semibold tracking-tight text-ps-dark-900 transition-colors group-hover:text-primary-500">{p.vorname ?? ''} {p.nachname ?? ''}</span>
                <div className="mt-1.5 flex items-center gap-3 text-[11px] text-ps-dark-500">
                  {aktenNr(p.patient_id) && <span>{aktenNr(p.patient_id)}</span>}
                  {p.station && <><span className="text-ps-dark-400">·</span><span className="truncate">{p.station}</span></>}
                </div>
              </div>
              <div className="numerical text-right text-[11px]">
                <div className="font-mono text-ps-dark-700">{fmtDate(p.geburtsdatum)}</div>
                <div className="text-ps-dark-400">{age(p.geburtsdatum)}{age(p.geburtsdatum) ? ' · ' : ''}{p.doc_count ?? p.file_count} Dok.</div>
              </div>
            </button>
          ))}
          {!loading && mehr && (
            <div className="px-5 py-3 text-center text-[12px] text-ps-dark-400">Weitere Akten vorhanden — Suche eingrenzen (Name, Akten-Nr., Geburtsdatum).</div>
          )}
        </div>
      </div>
    </div>
  );
}
